import { useCallback, useEffect, useState } from 'react';

import { useAuth } from '@opendoor/auth-fe/context';
import {
  getExperimentAssignment,
  recordExperimentImpression,
} from 'components/api/zeusExperiments';
import { getLocalFeatureFlagOverrides } from 'components/helpers/hooks';
import { useIsBot } from 'helpers/IsBotContext';
import { useObservability } from 'helpers/observability';

export type ExperimentAssignmentState = {
  /** The served arm's value, null until the assignment resolves. Compare against the flag's serialization ('true', ...); 'on' only ever comes from the QA-override normalization. */
  variant: string | null;
  /** Server-assigned variation index; set only for experiment-backed assignments. */
  variationId: number | null;
  /** False only while the first assignment for the current identity is in flight. */
  isResolved: boolean;
  /**
   * True when the value is a real experiment bucketing. A feature default,
   * force rule, QA override, or fail-closed control renders but must stay out
   * of every exposure stream.
   */
  isExperimentAssignment: boolean;
  /**
   * Records the exposure in both streams (Variant + journey). No-op unless the
   * assignment is experiment-backed; server-side idempotent. Call when the
   * viewer is actually exposed to the arm.
   */
  trackExposure: () => void;
};

type Options = {
  /** Experiment key; identical to the feature flag's id for flag-backed experiments. */
  experimentKey: string;
  /**
   * Whether this viewer can be exposed at all. An ineligible viewer is never
   * bucketed. Eligibility must be arm-independent so gating cannot bias the split.
   */
  enabled?: boolean;
  /** Honor the localStorage QA override (`opendoor.cosmos.<key>`); overridden sessions are never exposed. */
  honorLocalOverride?: boolean;
};

type Assignment = {
  /** Identity the arm was bucketed for; a served arm never crosses identities. */
  identity: string;
  variant: string | null;
  variationId: number | null;
  isFallback: boolean;
};

/** A served, experiment-backed assignment: real variant and variation index. */
type ConfirmedAssignment = Assignment & {
  variant: string;
  variationId: number;
  isFallback: false;
};

const confirmResolved = (resolved: Assignment | null): resolved is ConfirmedAssignment =>
  resolved != null &&
  !resolved.isFallback &&
  resolved.variant != null &&
  resolved.variationId != null;

// Dedupes evaluation per (experiment, identity, override posture) for the SPA
// session, so concurrent mounts share one read. Only experiment-backed
// assignments stay cached: getExperimentAssignment resolves undefined on every
// failure mode rather than rejecting, so a cached fallback would pin a
// transient blip (or a pre-launch "not bucketed") for the whole session. A
// remount after a failed or non-experiment read evaluates again.
const assignments = new Map<string, Promise<Assignment>>();

/** Exported for tests; the cache is otherwise SPA-session lifetime. */
export const __resetExperimentAssignmentCache = () => {
  assignments.clear();
  exposures.clear();
};

// One exposure per (experiment, identity) per session: the Variant impression
// is server-idempotent, but the paired journey event appends unconditionally.
const exposures = new Set<string>();

const evaluate = (
  experimentKey: string,
  identity: string,
  customerUuid: string | undefined,
  honorLocalOverride: boolean,
): Promise<Assignment> => {
  const cacheKey = `${experimentKey}|${identity}|${honorLocalOverride}`;
  const cached = assignments.get(cacheKey);
  if (cached) {
    return cached;
  }
  const pending = (async (): Promise<Assignment> => {
    if (honorLocalOverride) {
      const override = getLocalFeatureFlagOverrides(experimentKey);
      if (override != null) {
        return { identity, variant: override, variationId: null, isFallback: true };
      }
    }
    const assignment = await getExperimentAssignment(experimentKey, customerUuid);
    // Undefined means the request failed; a null value means the server
    // answered "not bucketed". Neither is a real assignment.
    if (assignment?.value == null) {
      return { identity, variant: null, variationId: null, isFallback: true };
    }
    return {
      identity,
      variant: assignment.value,
      variationId: assignment.variationId,
      isFallback: false,
    };
  })();
  assignments.set(cacheKey, pending);
  void pending.then((resolved) => {
    if (resolved.variationId == null) {
      assignments.delete(cacheKey);
    }
  });
  return pending;
};

/**
 * An experiment-backed feature assignment with exposure reporting.
 *
 * Reads through the experiment path (evaluateExperimentVariant) rather than
 * the plain BFF flag read: the BFF serves a bare value with no variationId and
 * records no exposure, so a GrowthBook experiment gated on it would run blind.
 */
export const useExperimentAssignment = ({
  experimentKey,
  enabled = true,
  honorLocalOverride = false,
}: Options): ExperimentAssignmentState => {
  const { user, authentication } = useAuth();
  const isBot = useIsBot();
  const { trackJourneyEvent } = useObservability();
  // Evaluating before the session settles buckets an about-to-sign-in visitor
  // under the anonymous id and re-buckets under the customer id, exposing one
  // arm while serving the other.
  const isAuthSettled =
    authentication.state === 'authenticated' || authentication.state === 'not_authenticated';
  const customerUuid = user?.customerUuid ?? undefined;
  const identity = customerUuid ?? 'anonymous';
  const [assignment, setAssignment] = useState<Assignment | null>(null);

  useEffect(() => {
    let cancelled = false;

    // Crawlers are excluded entirely: initObservability withholds the Journey
    // client for them, so a bucketed bot would log a Variant exposure the
    // journey stream never records, splitting the two logs against each other.
    if (!enabled || isBot || !isAuthSettled) {
      return;
    }

    void evaluate(experimentKey, identity, customerUuid, honorLocalOverride).then((resolved) => {
      if (!cancelled) {
        setAssignment(resolved);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [experimentKey, enabled, identity, customerUuid, isBot, isAuthSettled, honorLocalOverride]);

  // An arm resolved for a previous identity is withheld rather than served, so
  // a mid-page sign-in renders control until the replacement assignment lands.
  const resolved = assignment?.identity === identity ? assignment : null;
  const isExperimentAssignment = confirmResolved(resolved);

  const trackExposure = useCallback(() => {
    if (!confirmResolved(resolved)) {
      return;
    }
    const exposureKey = `${experimentKey}|${resolved.identity}`;
    if (exposures.has(exposureKey)) {
      return;
    }
    exposures.add(exposureKey);
    void recordExperimentImpression(
      experimentKey,
      { variant: resolved.variant, variationId: resolved.variationId },
      customerUuid,
    );
    // GrowthBook's assignment query joins exposures on variation_id and drops
    // nulls, so the journey impression carries the server-assigned index.
    trackJourneyEvent('experimentation', 'impression', {
      experimentName: experimentKey,
      variant: resolved.variant,
      variationId: resolved.variationId,
    });
  }, [resolved, experimentKey, customerUuid, trackJourneyEvent]);

  return {
    variant: resolved?.variant ?? null,
    variationId: resolved?.variationId ?? null,
    isResolved: resolved != null,
    isExperimentAssignment,
    trackExposure,
  };
};
